import React, { useState, useEffect, useLayoutEffect } from 'react'

import parse from 'html-react-parser';
import { ROUTES } from 'routes';
import { Link as RouterLink } from 'react-router-dom';

import { Timeline, TimelineSeparator, TimelineConnector, TimelineContent, TimelineDot } from '@mui/lab';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import { useTheme, Stack, Box, Divider, Typography, Link, Tooltip, CircularProgress  } from '@mui/material';

import { ActionLine, EmptyState } from 'components';

import { Avatar, hideScrollbar, Skeleton, useScrollFade } from '@repo/ui'

import { ActivityIcon, SearchXIcon } from 'lucide-react'
import { useReferrerState } from 'hooks'
import { datetime, timeAgo, multiSort } from 'utils';
import { zoomOf } from 'utils/textZoom';

export interface ActivityUser {
  id: string;
  full_name: string | null;
  avatar: string | null;
  username?: string | null;
  role: {
    /** Raw role name used for routing (e.g. "company_admin"). */
    name: string;
    /** Optional human-readable display label (e.g. "Admin"). Falls back to name. */
    displayName?: string;
  } | null;
}

export interface ActivityRow {
  /**
   * The person who acted, when there IS one.
   *
   * OPTIONAL because not every activity feed is an audit log. The company log's actors are
   * always admins; the certificate feed's are mostly NOT — a scan is usually an anonymous
   * visitor tapping a tag with a phone, and a transfer is often an owner, neither of whom has
   * a company-user account. Those rows still have an actor in the human sense ("Someone", "An
   * owner"), just not one that resolves to a user page.
   */
  user?: ActivityUser;
  action: string;
  result: string;
  date: string;
  /**
   * Optional category icon shown in the timeline gutter (e.g. a card for a
   * payment change, a shield for a permission change). Each consumer owns its
   * action→icon mapping; when omitted, a plain dot is shown. Keeps the shared
   * component generic so the company log and certificate activity stay in parity.
   */
  icon?: React.ReactNode;
}

export interface ActivityColumn {
  field: string;
  headerName: string;
  type?: string;
  sortable?: boolean;
}

type ActivityProps = {
  columns?: ActivityColumn[];
  activity?: ActivityRow[];
  loading?: boolean;
  emptyTitle?: string;
  emptyText?: string;
  sx?: object;
  // Server-side integration props — all optional so CertificatePage is unaffected.
  extraFilters?: React.ReactNode;
  onFiltersApply?: () => void;
  extraFiltersDirty?: boolean;
  onClearExtraFilters?: () => void;
  /** When provided, called in addition to internal sort state update. */
  onSortChange?: (col: any, direction?: string, override?: boolean) => void;
  /** When true, skips client-side multiSort (server already returns sorted rows). */
  serverSide?: boolean;
  /**
   * When provided, called on every search input change so the parent can issue
   * a server-side search. In this mode client-side filtering is skipped — the
   * server-returned `activity` array is treated as authoritative.
   * When absent, the existing client-side search behaviour is preserved
   * (CertificatePage compatibility).
   */
  onSearchChange?: (value: string) => void;
  /**
   * Optional controls rendered immediately LEFT of the Filter button via
   * ActionLine's beforeFiltersComponents slot (e.g. the export split-button).
   * When omitted (CertificatePage), nothing changes.
   */
  headerActions?: React.ReactNode;
  /** Infinite scroll: more pages exist to load. */
  hasMore?: boolean;
  /** Infinite scroll: called when the bottom sentinel scrolls into view. */
  onLoadMore?: () => void;
  /** Infinite scroll: a next-page fetch is in flight (shows a bottom loader). */
  loadingMore?: boolean;
  /** Confine the list to the viewport (fill to 24px above the bottom) and scroll it
   *  internally — like the DataTable's fillBottom — instead of growing the page.
   *  Opt-in so other consumers (e.g. the settings activity log) are unaffected. */
  fillHeight?: boolean;
  /** Hide the built-in ActionLine (search/sort/filter). Use when the parent page
   *  already provides its own toolbar (e.g. the Scans tab's Timeline view). */
  showActionLine?: boolean;
  /**
   * How much of the actor to show. Three real cases, so a three-way choice rather than two
   * booleans that can contradict each other:
   *
   * - `full` (default) — avatar + name + role. The company audit log, where the actor VARIES
   *   and is always a company user with a page to link to.
   * - `name` — the name only. The certificate feed, where the actor still varies ("You", "An
   *   owner", "Someone") but has no avatar to show: those rows are mostly customers and
   *   anonymous visitors with no account, so the circle renders generated initials — a
   *   coloured disc conveying nothing, repeated down the page, next to a category icon that
   *   IS informative.
   * - `none` — nothing. A feed already scoped to ONE person, where the actor is a constant.
   *   Repeating an identical avatar and name down twenty rows restates the page title once
   *   per row, in the leftmost and most-scanned position, stealing width from the action and
   *   target — the only things that vary.
   *
   * NOT the same as an actor being absent: `ActivityRow.user` is optional because some rows
   * have no account behind them. This is a presentation decision the caller makes.
   */
  actor?: 'full' | 'name' | 'none';
}

/**
 * Plain text of a `result` HTML fragment, for a title attribute on a truncated row.
 *
 * Deliberately crude — these fragments are built by this app's own mappers and are a handful
 * of spans and entities, not arbitrary markup. It exists so a clipped value is still readable
 * on hover, not to render anything.
 */
const resultPlainText = (html: string): string =>
  html
    .replace(/<[^>]*>/g, '')
    .replace(/&rarr;/g, '→')
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .trim();

const SKELETON_ROW_COUNT = 6;

// Skeleton mirrors the real Timeline layout (gutter dot + connector + content row)
// so the transition to loaded content causes no shift — the "weird jump" was the
// old flat skeleton (no gutter) reflowing when the timeline dots/connectors appeared.
const ActivitySkeletonRows = ({ actor }: { actor: 'full' | 'name' | 'none' }) => {
  const theme = useTheme()
  return (
    <Timeline
      sx={{
        [`& .${timelineItemClasses.root}:before`]: { flex: 0, padding: 0 },
      }}
    >
      {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
        <TimelineItem key={i}>
          <TimelineSeparator>
            <Skeleton variant="circular" width={36} height={36} />
            {i !== SKELETON_ROW_COUNT - 1 && (
              <TimelineConnector sx={{ bgcolor: theme.palette.grey[300], width: '1px' }} />
            )}
          </TimelineSeparator>
          {/* Mirrors the loaded row exactly (§19): same `alignSelf`/`minHeight` so the dot
              and the bars sit where the real ones will, same actor tier so a feed with no
              avatar does not shift left when it loads, and the date on the right edge. */}
          <TimelineContent sx={{ alignSelf: 'flex-start', py: 0, minHeight: 36, display: 'flex', alignItems: 'center' }}>
            <Stack direction="row" justifyContent="space-between" spacing={6} alignItems="center" sx={{ width: '100%' }}>
              <Stack direction="row" spacing={2} alignItems="center">
                {actor === 'full' && <Skeleton variant="circular" width={36} height={36} />}
                {actor !== 'none' && <Skeleton variant="rounded" width={90} height={12} />}
                <Skeleton variant="rounded" width={130} height={12} />
              </Stack>
              <Skeleton variant="rounded" width={70} height={10} />
            </Stack>
          </TimelineContent>
        </TimelineItem>
      ))}
    </Timeline>
  )
}

export const Activity = ({
  activity = [],
  columns = [],
  loading = false,
  emptyTitle = "No certificate activity",
  emptyText = "Perform an action to view certificate activity.",
  sx,
  extraFilters,
  onFiltersApply,
  extraFiltersDirty,
  onClearExtraFilters,
  onSortChange: onSortChangeProp,
  serverSide = false,
  onSearchChange,
  headerActions,
  hasMore = false,
  onLoadMore,
  loadingMore = false,
  fillHeight = false,
  showActionLine = true,
  actor = 'full',
}: ActivityProps) => {
  const theme = useTheme()
  // §42: the user/role links below jump out of the activity feed into another
  // record, so they hand the destination a way back. Derived from the current
  // location rather than passed in, because this component renders on more than
  // one surface (the company Activity log, certificate activity) and each should
  // return to its own page.
  const referrer = useReferrerState()
  const [search, setSearch] = useState("")
  const loadMoreRef = React.useRef<HTMLDivElement | null>(null)
  // Bottom fade. This was the one site of four that never wrote the
  // is-there-more-below bookkeeping, so its fade was PERMANENT — the last row
  // stayed dissolved even when you'd scrolled to it, reading as "there's more"
  // forever. The shared hook clears it at the bottom.
  const { ref: scrollBoxRef, fadeSx } = useScrollFade({ fadeHeight: 96, enabled: fillHeight })

  // fillHeight: size the scroll box so its bottom sits 24px above the viewport
  // bottom (same as the DataTable), then it scrolls internally. Measured (not a
  // hardcoded offset) so it's robust to the header/tabs height; re-measures on resize.
  const [fillMaxHeight, setFillMaxHeight] = useState<number | undefined>(undefined)
  useLayoutEffect(() => {
    if (!fillHeight) return
    const measure = () => {
      const el = scrollBoxRef.current
      if (!el) return
      // `top` and `innerHeight` are PAINTED px; `maxHeight` is written as LAYOUT px
      // inside `#app`'s text zoom. Convert the painted room to layout px before taking
      // the 24px gutter off (the gutter is layout px — it scales with everything else).
      // Unconverted, the box stopped (1 − z) short of the gutter at z < 1 and ran past
      // the bottom of the window at z > 1.
      const top = el.getBoundingClientRect().top
      setFillMaxHeight(Math.max(160, (window.innerHeight - top) / zoomOf(el) - 24))
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [fillHeight, activity.length > 0])
  const [sort, setSort] = useState<Array<{ field: string; sort: string }>>([])

  // When onSearchChange is provided, the parent drives server-side search and
  // the returned activity is already filtered — skip client-side filtering.
  // Without onSearchChange we apply the existing client-side filter (CertificatePage).
  let filteredActivity = onSearchChange
    ? activity
    : activity.filter(a =>
        JSON.stringify([
          a.user?.full_name,
          a.user?.role?.name,
          (a.user as ActivityUser & { username?: string })?.username,
          a.action,
          a.result,
        ].join(" ")).toLowerCase().includes(search.toLowerCase())
      )

  // client-side multi-sort — skipped when serverSide=true (server returns sorted rows)
  if (!serverSide) {
    filteredActivity = multiSort(filteredActivity, sort.reduce<Record<string, string>>((acc, curr) => {
      acc[curr.field] = curr.sort;
      return acc;
    }, {}))
  }

  const handleSortChange = (col: any, direction?: string, override?: boolean) => {
    // catch for override whole sort
    if (Array.isArray(col)) {
      setSort(col)
      onSortChangeProp?.(col, direction, override)
      return
    }
    // catch for clear
    if (col === 'all' && direction === 'clear') {
      setSort([])
      onSortChangeProp?.(col, direction, override)
      return
    }
    let newSort = [...sort]
    if (override) {
      newSort = [{ field: col, sort: direction as string }]
    } else {
      const existingSortIndex = newSort.findIndex(s => s.field === col);
      if (existingSortIndex !== -1) {
        newSort[existingSortIndex].sort = direction as string;
      } else {
        newSort.push({ field: col, sort: direction as string });
      }
    }
    setSort(newSort)
    onSortChangeProp?.(col, direction, override)
  }

  const showEmpty = !loading && activity.length === 0;

  // Delay the skeleton so a fast load (local/cached) doesn't flash it. It only
  // appears if we're still loading with no data after 250ms; otherwise the content
  // arrives first and the user never sees a skeleton.
  const isLoadingEmpty = loading && activity.length === 0;
  const [showSkeleton, setShowSkeleton] = useState(false);
  useEffect(() => {
    if (!isLoadingEmpty) {
      setShowSkeleton(false);
      return;
    }
    const timer = setTimeout(() => setShowSkeleton(true), 250);
    return () => clearTimeout(timer);
  }, [isLoadingEmpty]);

  // Infinite scroll: load the next page when scrolled within 300px of the bottom.
  // No-op unless the parent wires hasMore + onLoadMore (so the company log etc. are
  // unaffected). The cert page scrolls an inner container (not the window), so we
  // attach to the nearest scrollable ancestor. A scroll listener (vs
  // IntersectionObserver) is used because it's deterministic and fires reliably.
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el || !hasMore || !onLoadMore) return;
    let scroller: HTMLElement | null = el.parentElement;
    while (scroller) {
      const style = getComputedStyle(scroller);
      if (/(auto|scroll)/.test(style.overflowY) && scroller.scrollHeight > scroller.clientHeight) break;
      scroller = scroller.parentElement;
    }
    const target: HTMLElement | Window = scroller ?? window;
    const check = () => {
      if (loadingMore) return;
      const sc = scroller ?? document.scrollingElement;
      if (!sc) return;
      if (sc.scrollTop + sc.clientHeight >= sc.scrollHeight - 300) onLoadMore();
    };
    target.addEventListener('scroll', check, { passive: true });
    check(); // fill the viewport if the first page is short / already near bottom
    return () => target.removeEventListener('scroll', check);
  }, [hasMore, onLoadMore, loadingMore, activity.length]);

  // A search term or an applied extra filter (role / date) is active — so an empty
  // result is "nothing matched your filters", not "no activity has ever happened".
  const filtersActive = search.trim() !== "" || !!extraFiltersDirty;

  // Distinct empty state for a filtered-to-zero result: a search graphic + "No
  // results", not the "No activity yet" first-run state.
  const noResultsState = (
    <Stack alignItems="center" justifyContent="center" sx={{ minHeight: '100%', pt: 32 }}>
      <EmptyState
        title="No results"
        text="Try adjusting your search or filters."
        icon={<SearchXIcon strokeWidth={1} size={32} />}
      />
    </Stack>
  );

  return (
    <Stack>
      {showActionLine && (
        <ActionLine
          showBulkActions={false}
          // Match the Scans tab's ActionLine exactly — no loading-driven disabling of
          // search / sort / filter. Disabling on the initial load made the controls
          // flash dimmed→enabled on tab-switch (the search bar in particular). The list
          // skeleton below already conveys loading, so the toolbar stays visually stable
          // and identical across tabs (same position, same enabled look, no flash).
          showSearch={true}
          showSort={true}
          columns={columns}
          onSearchChange={(value) => { setSearch(value); onSearchChange?.(value); }}
          sort={sort}
          onSortChange={handleSortChange}
          extraFilters={extraFilters}
          onFiltersApply={onFiltersApply}
          extraFiltersDirty={extraFiltersDirty}
          onClearExtraFilters={onClearExtraFilters}
          beforeFiltersComponents={headerActions}
        />
      )}

      <Box
        ref={scrollBoxRef}
        // fillHeight (cert Activity tab): a bordered, internally-scrolling card that
        // matches the sibling Provenance DataTable 1:1 — same 1px primary[100] border
        // + 12px radius, same 24px bottom gap (fillMaxHeight), same ~17px gap below the
        // ActionLine. The border is what makes the (already-correct) margins legible:
        // without it, the top padding reads as dead space and rows scrolled to the
        // box edge look cut off into nothing. Scrollbar hidden for parity with the grid.
        sx={fillHeight ? {
          overflowY: 'auto',
          minHeight: 0,
          // FIXED height (not maxHeight) so the list ALWAYS fills to 24px above the
          // viewport bottom — even when empty / loading / few rows — then scrolls
          // internally. (maxHeight collapsed to short content on first load.)
          height: fillMaxHeight,
          // Full-bleed (no border): the timeline's connector line is its own spine,
          // so a card border reads as redundant framing. Instead, soft-fade the
          // bottom edge so rows scrolling past the fold dissolve rather than getting
          // hard-clipped at an invisible boundary.
          ...fadeSx,
          ...hideScrollbar,
        } : undefined}
      >
      {showSkeleton && <ActivitySkeletonRows actor={actor} />}

      {showEmpty && (filtersActive ? noResultsState : (
        <Stack alignItems="center" justifyContent="center" sx={{ minHeight: '100%', pt: 32 }}>
          <EmptyState
            title={emptyTitle}
            text={emptyText}
            icon={<ActivityIcon strokeWidth={1} size={32} />}
          />
        </Stack>
      ))}

      {!loading && activity.length > 0 && (
        <Stack>
          {filteredActivity.length === 0 && noResultsState}
          {filteredActivity.length > 0 && (
            <Timeline
              sx={{
                [`& .${timelineItemClasses.root}:before`]: {
                  flex: 0,
                  padding: 0,
                },
              }}
            >
              {filteredActivity.map((a, i) => {
                const result = parse(a.result, { htmlparser2: { lowerCaseTags: false } })
                const plainResult = resultPlainText(a.result)
                return (
                  <TimelineItem key={a.user?.id + i}>
                    <TimelineSeparator>
                      {a.icon ? (
                        <TimelineDot sx={{
                          /*
                            36px, up from 28, and NO `mt`.
                            
                            The `mt: 3` was nudging the dot 12px down while the content beside
                            it stayed top-aligned, so icon and text never sat on the same
                            line — and the taller the row (a wrapping value), the worse the
                            drift. Alignment is handled properly now by centring the CONTENT
                            against the dot (see TimelineContent below) rather than by
                            offsetting the dot and hoping the two agree.
                          */
                          m: 0,
                          width: 36,
                          height: 36,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: `1px solid ${theme.palette.grey[300]}`,
                          backgroundColor: theme.palette.white[100],
                          color: theme.palette.text.secondary,
                          boxShadow: 'none',
                          '& svg': { width: 18, height: 18 },
                        }}>
                          {a.icon}
                        </TimelineDot>
                      ) : (
                        <TimelineDot color="primary" sx={{ marginTop: 4, border: `4px solid ${theme.palette.grey[900]}`, backgroundColor: theme.palette.white[100] }} />
                      )}
                      {i !== filteredActivity.length - 1 && <TimelineConnector sx={{ bgcolor: theme.palette.grey[300], width: '1px' }} />}
                    </TimelineSeparator>
                    <TimelineContent
                      sx={{
                        /*
                          THE 17px OFFSET, and why two earlier fixes moved it by exactly zero
                          pixels: MUI gives `TimelineItem` a `min-height: 70px`. The dot is
                          anchored at the TOP of that box, but `alignItems: center` on a
                          STRETCHED TimelineContent centres the text in the whole 70 — so the
                          text sat at 35 and the dot's centre at 18. 70/2 − 36/2 = 17, exactly
                          the measured gap. Both previous attempts adjusted padding INSIDE the
                          content, which cannot touch either of those two numbers.

                          `alignSelf: flex-start` is the fix: the content stops stretching to
                          the item's 70px and becomes exactly as tall as the dot (minHeight 36),
                          so centring within it puts both centres on 18. The item's 70px still
                          sets row RHYTHM, which is what it is good for.
                        */
                        alignSelf: 'flex-start',
                        py: 0,
                        display: 'flex',
                        alignItems: 'center',
                        // 36 — the dot's own diameter, which is also the actor avatar's. All
                        // three matching is what makes the offset exactly 0 rather than
                        // nearly 0: a 40px avatar made the content 40 tall and put every row
                        // 2px (40/2 − 36/2) below its dot on the company log.
                        minHeight: 36,
                        // Without this the flex child below sizes to its content, and
                        // `justifyContent: space-between` has no free space to distribute —
                        // which is why the dates sat next to the text instead of at the row's
                        // right edge.
                        minWidth: 0,
                      }}
                    >
                        {/* No entrance fade — the row (content) appears together with
                            its timeline gutter (dot + connector). Loading is covered by
                            ActivitySkeletonRows, per the skeleton convention. */}
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          spacing={6}
                          sx={{ width: '100%', minWidth: 0 }}
                        >
                          <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                            sx={{ minWidth: 0, flex: 1 }}
                          >
                            {/*
                              LINKED ONLY WHEN THERE IS A USER TO LINK TO. This was an
                              unconditional <Link> to SETTINGS_COMPANY_USER(a.user?.id), which
                              produced a live link to `/settings/users/undefined` for any row
                              without an account behind it — and that is the MAJORITY of the
                              certificate feed, where most scans are anonymous. It also
                              mis-linked this page's own `System` pseudo-actor, which carries
                              `id: ''`, to `/settings/users/`.
                            */}
                            {actor !== 'none' && (a.user?.id ? (
                              <Link component={RouterLink} to={ROUTES.SETTINGS_COMPANY_USER(a.user.id)} state={referrer} underline="none" sx={{ cursor: 'pointer', color: 'inherit', flexShrink: 0, '&:hover .MuiTypography-root': { textDecoration: 'underline' } }}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                  {actor === 'full' && <Avatar src={a.user.avatar} name={a.user.full_name} size={36} />}
                                  <Typography variant="body3" noWrap>{a.user.full_name}</Typography>
                                </Stack>
                              </Link>
                            ) : (
                              <Stack direction="row" spacing={2} alignItems="center" sx={{ flexShrink: 0 }}>
                                {actor === 'full' && <Avatar src={a.user?.avatar ?? null} name={a.user?.full_name ?? null} size={36} />}
                                <Typography variant="body3" noWrap>{a.user?.full_name}</Typography>
                              </Stack>
                            ))}
                            {actor === 'full' && a.user?.role?.name && (
                              <>
                                <Divider orientation="vertical" flexItem />
                                <Link component={RouterLink} to={ROUTES.SETTINGS_COMPANY_ROLE(a.user.role.name)} state={referrer} underline="none" sx={{ cursor: 'pointer', color: 'inherit', display: 'inline-flex', alignItems: 'center', '&:hover .MuiTypography-root': { textDecoration: 'underline' } }}>
                                  <Typography variant="body3" noWrap>{a.user.role.displayName ?? a.user.role.name}</Typography>
                                </Link>
                              </>
                            )}
                            <Typography variant="body3" color="text.secondary" noWrap sx={{ flexShrink: 0 }}>{a.action}</Typography>
                            {/*
                              THE VALUE IS THE PART THAT TRUNCATES, and it is the only part:
                              every sibling above is `flexShrink: 0`, so a long value clips
                              instead of squeezing the actor and action out of the row.

                              This replaced `overflowX: 'auto'` + `width: 'fit-content'`, which
                              let a row grow without bound — a logo change renders two full GCS
                              URLs and measured ~1900px wide, dragging its own timestamp off
                              past the right edge of the page and taking the date column's
                              alignment with it.

                              Mappers shorten what they can (a URL renders as its filename), so
                              this is the backstop for values no mapper anticipated. The title
                              attribute keeps a clipped value readable on hover, and is set only
                              when the text is actually long enough to be at risk — a native
                              tooltip on every short row is just noise following the cursor.
                            */}
                            <Box
                              title={plainResult.length > 60 ? plainResult : undefined}
                              sx={{
                                typography: 'body3',
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                // The parsed fragment is spans and entities; keep them on one
                                // line so the ellipsis lands at the row's edge.
                                '& *': { display: 'inline' },
                                ...hideScrollbar,
                              }}
                            >
                              {result}
                            </Box>
                          </Stack>
                          {/*
                            The date column: `flexShrink: 0` so it is never squeezed, and
                            right-aligned so every row's timestamp ends on the same edge —
                            "~1 hour ago" and "28 days ago" are different widths, and ragging
                            them left made the column look accidental.
                          */}
                          <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ flexShrink: 0 }}>
                            <Tooltip title={datetime(a.date)} placement="left" arrow>
                              <Typography variant="body3" color="text.secondary" noWrap sx={{ cursor: 'default', textAlign: 'right' }}>{timeAgo(a.date)}</Typography>
                            </Tooltip>
                          </Stack>
                        </Stack>
                    </TimelineContent>
                  </TimelineItem>
                )
              })}
            </Timeline>
          )}

          {/* Infinite scroll: this zone is both the observed sentinel (needs a
              non-zero height — a 0px target doesn't fire IntersectionObserver) and
              the loader that shows while the next page is in flight. Only rendered
              when the parent wires hasMore. */}
          {hasMore && filteredActivity.length > 0 && (
            <Stack
              ref={loadMoreRef}
              alignItems="center"
              justifyContent="center"
              sx={{ minHeight: 48, py: 2 }}
            >
              {loadingMore && (
                <CircularProgress size={20} sx={{ color: theme.palette.text.secondary }} />
              )}
            </Stack>
          )}
        </Stack>
      )}
      </Box>
    </Stack>
  );
}
